/**
 * The enrolment funnel: pitch, confirm dialog, celebration.
 *
 * Rendering only. The state machine behind it -- the reads, the consent flag, the write and which
 * surface is showing -- is useJoinFunnel; this file decides what each surface is handed.
 */

import { fade } from '@drewling/urc-ui-core/styles';
import { JoinConfirmModal, LoyaltyJoinHero, LoyaltyJoinSuccess } from '@drewling/urc-loyalty-ui';
import { GENERIC_URC_TEAM } from '../genericTeam';
import { signInHref } from '../loginUrl';
import { accessToken, login } from '../urcAuth';
import { useJoinFunnel } from '../useJoinFunnel';
import { useLoyaltySession } from '../useLoyaltySession';
import { useSignedIn } from '../useSignedIn';
// Reserves the band's height on the empty mount container, so the deferred bundle costs no layout
// shift. The heights, the mount-scoped selector and what still moves on mobile are in the file.
import '../joinHero.css';

/**
 * @param {Object}  props
 * @param {Object}  props.labels        Grouped editor copy: { hero, signedOut, confirm, success }.
 * @param {string=} props.profileUrl    Target of the "Visit profile" button, or undefined.
 * @param {string=} props.termsUrl      Target of the terms link in the confirm dialog.
 * @param {string=} props.termsTarget   '_blank' when the editor asked for a new tab.
 * @param {number=} props.welcomePoints Bonus authored on the block, or undefined for the API's.
 */
export function JoinBlock( { labels = {}, profileUrl, termsUrl, termsTarget, welcomePoints } ) {
	const signedIn = useSignedIn();
	const session = useLoyaltySession();

	/*
	 * **What the fan SEES follows the site header, exactly.** Both now read the same thing --
	 * `window.urcAuth`, whose state comes from an ID token the SDK has validated -- so this button
	 * and the `My URC` control above it cannot contradict each other, on a cached page included.
	 *
	 * The two reasons this was ever a hard question have both gone. It used to gate on
	 * `session.authenticated`, and the reasoning (D-045) was sound: joining is a WRITE, and the
	 * `auth0_authenticated` cookie was an unsigned impression that outlived the credential behind
	 * it. What broke that was not the reasoning but the READ -- a third-party script expired every
	 * `auth0_*` cookie on `DOMContentLoaded`, this component's session read is a Preact effect
	 * (after paint, so after the clear) and the header read during parse, so the block saw "no
	 * token" for a fan the header was calling `My URC`, permanently. There is no cookie to delete
	 * now, and "signed in" and "has a credential" are answers from one source rather than two
	 * unrelated ones.
	 *
	 * The credential question has still not been dropped: `startJoin()` below asks for a real token
	 * before opening the dialog.
	 */
	const canJoin = signedIn;

	const {
		phase,
		enrolment,
		bonusPoints,
		credited,
		startingTier,
		consented,
		submitting,
		submitError,
		openConfirm,
		closeConfirm,
		setConsented,
		submit,
		// The RESOLVED answer here, not the optimistic one: every read below already waits on this
		// same session request for its token, so holding them until it settles costs no latency
		// and spares a fan with no token three rejections that were never going to succeed.
	} = useJoinFunnel( session?.authenticated === true );

	// A fan with no club can still join, so the URC's own mark stands in for the club card.
	// `undefined` is left alone rather than folded in with null: it means the session request is
	// still in flight, and flashing generic branding before the fan's real club arrives is the
	// exact defect the session's three team states exist to prevent.
	const branding = session === undefined ? undefined : session.team || GENERIC_URC_TEAM;

	/*
	 * The click is where the credential is checked, and the check is made AT the click rather than
	 * read from something established earlier in the page. That is the property D-065 named, and it
	 * survives the move: what changes is that the answer is awaited rather than returned.
	 *
	 * **Awaiting it is not a weakening of that property, it is the stronger version of it.** The old
	 * read was synchronous because it was a cookie parse plus this page's captured token -- current
	 * by construction, but only because the module had gone to some trouble to hold a credential a
	 * third-party script kept deleting. `getAccessToken()` asks the SDK, which answers from its own
	 * cache in a microtask on any ordinary page and mints a fresh token when the cached one has
	 * expired. A click cannot be raced either way; this one also cannot be answered by a token that
	 * has since run out.
	 *
	 * A fan with a usable token gets the dialog. One without is sent to Auth0, which is where the
	 * signed-out CTA points anyway. `login()` rather than the href: the fan has already committed
	 * to the navigation, and the SDK's own redirect carries `state`, `nonce` and PKCE and keeps
	 * `returnTo` in its storage instead of on the URL. With no contract at all there is nowhere to
	 * send them, so the dialog opens and the write reports its own failure -- better than a control
	 * that does nothing.
	 */
	const startJoin = () => {
		accessToken().then( token => {
			if ( token ) {
				openConfirm();

				return;
			}

			login( window.location.href );
		} );
	};

	// JoinConfirmModal renders the terms anchor itself and takes no `target`, so the editor's
	// "open in a new tab" is honoured through the click hook it does expose. Worth honouring: the
	// dialog holds an unfinished join, and navigating the tab away throws it out. `noopener` for
	// the usual reason -- the terms may be off-site.
	const onTermsClick =
		termsUrl && termsTarget === '_blank'
			? event => {
					event.preventDefault();
					window.open( termsUrl, '_blank', 'noopener' );
			  }
			: undefined;

	if ( phase === 'success' ) {
		if ( credited === null ) {
			return null;
		}

		return (
			<LoyaltyJoinSuccess
				tierName={ startingTier }
				welcomePoints={ credited }
				headingLevel={ 1 }
				profileHref={ profileUrl }
				labels={ labels.success }
				// The hero unmounts and this mounts in the same commit -- different vnode types,
				// so Preact builds a fresh element and the library's own mount fade runs. CSS
				// only, reduced motion included; see @drewling/urc-ui-core/styles/fade.module.scss.
				className={ fade.fadeIn }
			/>
		);
	}

	// Nothing to join: the tenant's master challenge is not provisioned, so a CTA here could only
	// ever reach 503 master_challenge_unconfigured. The library ships no block for this state and
	// this block has no field for a coming-soon message, so rendering nothing is the honest
	// answer -- inventing uneditable copy inside a JS bundle is not. Unreachable while signed out,
	// where the read rejects before the wire and `enrolment` stays null.
	if ( enrolment === 'unconfigured' ) {
		return null;
	}

	return (
		<>
			<LoyaltyJoinHero
				// 'unknown' and the pre-read null keep the join CTA: the route is idempotent, so
				// offering a join to a fan we cannot classify costs a redundant write at worst,
				// while hiding it would lock out a fan who could still join.
				enrolled={ enrolment === 'enrolled' }
				headingLevel={ 1 }
				profileHref={ profileUrl }
				/*
				 * A visitor the header calls signed out gets a LINK to Auth0; everyone else gets a
				 * button, and `startJoin()` decides what the button does. Exactly one side of the
				 * pair is ever passed, because the library renders an <a> the moment it has an
				 * href and hangs onJoinClick off that same anchor. (When `enrolled` is true the
				 * library ignores both and uses the profile pair instead, which is right for a fan
				 * in that state either way.)
				 */
				joinHref={ canJoin ? undefined : signInHref() }
				onJoinClick={ canJoin ? startJoin : undefined }
				/*
				 * `signedOut` is not a library surface: it is an overlay of library keys, spread
				 * over `hero` only while the control is a sign-in link. Authored separately in ACF
				 * so the two CTAs can be worded independently, and merged here rather than in PHP
				 * so nothing non-library ever reaches the component.
				 */
				labels={ canJoin ? labels.hero : { ...labels.hero, ...labels.signedOut } }
			/>
			{ /*
				`branding` is undefined only while the session request is in flight, so a click
				that lands first is pending rather than dropped -- the dialog mounts as soon as it
				resolves. The bonus is NOT gated: welcomePoints is optional, and a fan whose
				/api/challenges read failed gets a working dialog with no points chip rather than
				a dead button (D-044).

				Not gated on `canJoin` either. The phase is only reachable from a CTA that only
				exists when a session is usable, and adding it here would unmount an open dialog.
			*/ }
			{ branding !== undefined && (
				<JoinConfirmModal
					open={ phase === 'confirm' }
					onClose={ closeConfirm }
					team={ branding }
					startingTierName={ startingTier }
					/*
					 * The authored figure WINS where there is one, and the API's standing offer
					 * is what shows otherwise (D-084). Blank is the normal setting: the block
					 * omits the key rather than sending 0 or '' (D-039), so `undefined` here
					 * means "nobody authored one", never "the editor asked for no figure".
					 *
					 * This is an advertisement, and only ever that. The success screen still
					 * names `credited` -- what the join actually paid -- so an authored number
					 * that disagrees with the programme is visible as a disagreement rather than
					 * quietly propagated into the celebration.
					 */
					welcomePoints={ welcomePoints === undefined ? bonusPoints : welcomePoints }
					consented={ consented }
					onConsentChange={ setConsented }
					onSubmit={ submit }
					submitting={ submitting }
					submitError={ submitError }
					termsHref={ termsUrl }
					onTermsClick={ onTermsClick }
					labels={ labels.confirm }
				/>
			) }
		</>
	);
}
